import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import 'flag-icons/css/flag-icons.min.css';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import MainCategoryTable, { CATEGORY_LEVELS } from './components/CategoryTable';
import AllCategoryDialog from './components/CategoryDialog';
import CategoryTableFilter from './components/CategoryTableFilter';
import { useManageCategoryDialog } from './hooks/useManageCategoryDialog';
import { useCategoryList } from './hooks/useCategoryList';
import { useGetAllLanguagesQuery } from '../../../api/endpoints/language';
import { useLazyExportFaultCodesToExcelQuery } from '../../../api/endpoints/faultcodes';
import { useLanguage } from '../../../contexts/LanguageContext';
import GenericFileExportButton from '../../../components/common/GenericFileExportButton';

const URL_TO_LEVEL_MAP = {
    maincategory: CATEGORY_LEVELS.MAIN,
    secondcategory: CATEGORY_LEVELS.SECONDARY,
    subcategory: CATEGORY_LEVELS.SUB,
    subpartcategory: CATEGORY_LEVELS.SUB_PART,
    failmodecategory: CATEGORY_LEVELS.FAILURE_MODES
};

const categoryUiOptions = [
    { label: 'faultCodes.mainCategory', internalLabel: 'maincategory', value: CATEGORY_LEVELS.MAIN },
    { label: 'faultCodes.secondCategory', internalLabel: 'secondcategory', value: CATEGORY_LEVELS.SECONDARY },
    { label: 'faultCodes.subCategory', internalLabel: 'subcategory', value: CATEGORY_LEVELS.SUB },
    { label: 'faultCodes.subPartCategory', internalLabel: 'subpartcategory', value: CATEGORY_LEVELS.SUB_PART },
    { label: 'faultCodes.failureMode', internalLabel: 'failuremodescategory', value: CATEGORY_LEVELS.FAILURE_MODES }
];

// Kategori seviyesini backend RequestType (Category enum) değerine eşler.
// 1=MainCategory, 2=Secondary, 3=Sub, 4=SubPart, 5=FailureModes
const LEVEL_TO_REQUEST_TYPE = {
    [CATEGORY_LEVELS.MAIN]: 1,
    [CATEGORY_LEVELS.SECONDARY]: 2,
    [CATEGORY_LEVELS.SUB]: 3,
    [CATEGORY_LEVELS.SUB_PART]: 4,
    [CATEGORY_LEVELS.FAILURE_MODES]: 5
};

const LANGUAGE_TEXT_TO_CODE_MAP = {
    türkçe: 'tr',
    english: 'en',
    german: 'de',
    french: 'fr',
    italian: 'it',
    romanian: 'ro'
};

const transformToDropdownOptions = (items, labelField = 'text') => {
    if (!Array.isArray(items) || items.length === 0) return [];
    return items.map((item) => ({
        label: item.mainCategoryName || item.secondaryCategoryName || item.subCategoryName || `ID: ${item.id}`,
        value: item.id
    }));
};

const FaultCodesDefinitionsPage = () => {
    const { categoryLevelUrl } = useParams();
    const navigate = useNavigate();
    const toast = useRef(null);
    const { th, getCurrentLanguageId } = useLanguage();

    const [currentCategoryLevel, setCurrentCategoryLevel] = useState(() => URL_TO_LEVEL_MAP[categoryLevelUrl] || CATEGORY_LEVELS.MAIN);

    const [globalSearchText, setGlobalSearchText] = useState('');
    const [filters, setFilters] = useState({
        mainCategory: null,
        secondaryCategory: null,
        subCategory: null,
        subPartCategory: null
    });

    useEffect(() => {
        const levelFromUrl = URL_TO_LEVEL_MAP[categoryLevelUrl] || CATEGORY_LEVELS.MAIN;
        if (!URL_TO_LEVEL_MAP[categoryLevelUrl] && categoryLevelUrl) {
            if (categoryLevelUrl !== 'maincategory') {
                navigate(`/warranty-management/fault-codes/maincategory`, { replace: true });
            } else {
                setCurrentCategoryLevel(CATEGORY_LEVELS.MAIN);
            }
        } else {
            setCurrentCategoryLevel(levelFromUrl);
        }
    }, [categoryLevelUrl, navigate]);

    const { data: fetchedLanguagesFromApi, isLoading: isLoadingLanguages } = useGetAllLanguagesQuery();

    const supportedLanguagesForDialog = useMemo(() => {
        const languages = fetchedLanguagesFromApi || [];

        if (isLoadingLanguages || !Array.isArray(languages) || languages.length === 0) return [];

        return languages
            .map((langFromApi) => {
                if (!langFromApi || langFromApi.id === undefined || langFromApi.id === null || !langFromApi.text) {
                    return null;
                }

                const apiLangText = String(langFromApi.text).toLowerCase().trim();
                const langCode = LANGUAGE_TEXT_TO_CODE_MAP[apiLangText];

                if (!langCode) {
                    return null;
                }
                return {
                    id: langFromApi.id,
                    code: langCode,
                    name: langFromApi.text
                };
            })
            .filter(Boolean);
    }, [fetchedLanguagesFromApi, isLoadingLanguages]);

    const {
        items,
        isLoading: listIsLoading,
        error: listError,
        refetch: originalRefetch,
        lazyParams: tableLazyParamsFromHook,
        onPageChange: onTablePageChange,
        totalRecords,
        setContext: setTableContext
    } = useCategoryList({
        level: currentCategoryLevel,
        searchText: globalSearchText,
        filters: filters
    });

    const categoryDialog = useManageCategoryDialog(currentCategoryLevel, supportedLanguagesForDialog, () => {
        if (originalRefetch) originalRefetch();
        if (toast.current) {
            toast.current.show({
                severity: 'success',
                summary: th('warrantyClaimLimit.toastMessages.successSummary'),
                detail: th('warrantyClaimLimit.toastMessages.updateSuccess'),
                life: 3000
            });
        }
    });

    const { items: rawMainCategoriesForDialog, isLoading: isLoadingMainForDialog } = useCategoryList({
        level: CATEGORY_LEVELS.MAIN,
        RowCount: 500,
        skip: !categoryDialog.dialogVisible || categoryDialog.formData?.categoryLevel === CATEGORY_LEVELS.MAIN
    });

    const { items: rawSecondCategoriesForDialog, isLoading: isLoadingSecondForDialog } = useCategoryList({
        level: CATEGORY_LEVELS.SECONDARY,
        mainCategory: categoryDialog.formData?.mainCategoryId,
        RowCount: 500,
        skip: !categoryDialog.dialogVisible || categoryDialog.formData?.categoryLevel === CATEGORY_LEVELS.MAIN || categoryDialog.formData?.categoryLevel === CATEGORY_LEVELS.SECONDARY
    });

    const { items: rawSubCategoriesForDialog, isLoading: isLoadingSubForDialog } = useCategoryList({
        level: CATEGORY_LEVELS.SUB,
        mainCategory: categoryDialog.formData?.mainCategoryId,
        secondaryCategory: categoryDialog.formData?.secondCategoryId,
        RowCount: 500,
        skip: !categoryDialog.dialogVisible || categoryDialog.formData?.categoryLevel === CATEGORY_LEVELS.MAIN || categoryDialog.formData?.categoryLevel === CATEGORY_LEVELS.SECONDARY || categoryDialog.formData?.categoryLevel === CATEGORY_LEVELS.SUB
    });

    const mainCategoryOptionsForDialog = useMemo(() => transformToDropdownOptions(rawMainCategoriesForDialog), [rawMainCategoriesForDialog]);

    const secondCategoryOptionsForDialog = useMemo(() => transformToDropdownOptions(rawSecondCategoriesForDialog), [rawSecondCategoriesForDialog]);

    const subCategoryOptionsForDialog = useMemo(() => transformToDropdownOptions(rawSubCategoriesForDialog), [rawSubCategoriesForDialog]);

    useEffect(() => {
        setTableContext(
            currentCategoryLevel,
            {
                mainCategory: null,
                secondaryCategory: null,
                subCategory: null,
                subPartCategory: null
            },
            ''
        );
        setGlobalSearchText('');
        setFilters({
            mainCategory: null,
            secondaryCategory: null,
            subCategory: null,
            subPartCategory: null
        });
    }, [currentCategoryLevel, setTableContext]);

    const handleApplyFilters = useCallback(
        (newFilters, searchText) => {
            setFilters(newFilters);
            setGlobalSearchText(searchText || '');
            setTableContext(currentCategoryLevel, newFilters, searchText || '');
        },
        [currentCategoryLevel, setTableContext]
    );

    const handleResetFilters = useCallback(() => {
        const resetFilters = {
            mainCategory: null,
            secondaryCategory: null,
            subCategory: null,
            subPartCategory: null
        };
        setFilters(resetFilters);
        setGlobalSearchText('');
        setTableContext(currentCategoryLevel, resetFilters, '');
    }, [currentCategoryLevel, setTableContext]);

    const handleSort = useCallback((event) => {
        console.log('Sort event:', event);
    }, []);

    const getCurrentCategoryOption = () => {
        return categoryUiOptions.find((opt) => opt.value === currentCategoryLevel) || categoryUiOptions[0];
    };

    const currentOption = getCurrentCategoryOption();

    const exportParams = useMemo(
        () => ({
            RequestType: LEVEL_TO_REQUEST_TYPE[currentCategoryLevel] || 1,
            LanguageId: getCurrentLanguageId(),
            MainCategory: filters.mainCategory ?? null,
            SecondaryCategory: filters.secondaryCategory ?? null,
            SubCategory: filters.subCategory ?? null,
            SubPartCategory: filters.subPartCategory ?? null,
            SearchText: globalSearchText || null,
            CompressionType: 0
        }),
        [currentCategoryLevel, filters, globalSearchText, getCurrentLanguageId]
    );

    const getPageTitle = () => {
        return `${th(currentOption.label)} ${th('faultCodes.definitions')}`;
    };

    const getAddButtonLabel = () => {
        return th('faultCodes.add', { category: th(currentOption.label) });
    };

    if (listError) {
        return <div className="p-error text-center p-4">{`${th('common.dataLoadError')}: ${listError.data?.message || th('common.unknownError')}`}</div>;
    }

    const tableLazyParamsToUse = tableLazyParamsFromHook || { page: 0, rows: 10, first: 0 };

    return (
        <div className="card">
            <Toast ref={toast} />

            <div className="page-header mb-4">
                <div className="flex justify-content-between align-items-center">
                    <div>
                        <h1 className="page-title mb-2">{getPageTitle()}</h1>
                    </div>
                    <div className="flex gap-2">
                        <GenericFileExportButton
                            useExportQuery={useLazyExportFaultCodesToExcelQuery}
                            exportParams={exportParams}
                            buttonLabel={th('common.export') || 'Excel'}
                            buttonClassName="p-button-success"
                            icon="pi pi-file-excel"
                            fileNameExtractor={(result) => result?.fileName || 'fault_codes.xlsx'}
                            dataExtractor={(result) => result?.fileBase64}
                            contentTypeExtractor={(result) => result?.contentType}
                        />
                        <Button label={getAddButtonLabel()} icon="pi pi-plus" onClick={() => categoryDialog.openDialog({ categoryLevel: currentCategoryLevel }, false)} />
                    </div>
                </div>
            </div>

            <CategoryTableFilter currentCategoryLevel={currentCategoryLevel} filters={filters} onApplyFilters={handleApplyFilters} onResetFilters={handleResetFilters} transformToDropdownOptions={transformToDropdownOptions} />

            <MainCategoryTable
                items={items || []}
                loading={listIsLoading || (categoryDialog.dialogVisible && (isLoadingLanguages || isLoadingMainForDialog || isLoadingSecondForDialog || isLoadingSubForDialog))}
                lazyParams={tableLazyParamsToUse}
                totalRecords={totalRecords || 0}
                onPage={onTablePageChange}
                onSort={handleSort}
                handleEdit={(rowData) => categoryDialog.openDialog({ ...rowData, categoryLevel: currentCategoryLevel }, true)}
                categoryLevel={currentCategoryLevel}
            />

            {categoryDialog.dialogVisible && (
                <AllCategoryDialog
                    visible={categoryDialog.dialogVisible}
                    onHide={categoryDialog.hideDialog}
                    formData={categoryDialog.formData}
                    handleInputChange={categoryDialog.handleInputChange}
                    handleDropdownChange={categoryDialog.handleDropdownChange}
                    onSubmit={categoryDialog.handleSubmit}
                    formSubmitted={categoryDialog.submitted}
                    isSubmitting={categoryDialog.isProcessing}
                    isEditMode={categoryDialog.isEditMode}
                    supportedLanguages={supportedLanguagesForDialog}
                    dialogError={categoryDialog.mutationError?.data?.message || (categoryDialog.mutationError ? th('common.error') : null)}
                    categoryLevel={categoryDialog.formData?.categoryLevel || currentCategoryLevel}
                    executeDelete={categoryDialog.handleDeleteConfirm}
                    isDeleting={categoryDialog.isDeleting}
                    mainCategoryOptions={mainCategoryOptionsForDialog}
                    secondCategoryOptions={secondCategoryOptionsForDialog}
                    subCategoryOptions={subCategoryOptionsForDialog}
                />
            )}
        </div>
    );
};

export default FaultCodesDefinitionsPage;